import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioProgramaTurismo from './FormularioProgramaTurismo';

interface Props {
  idPlan: number;
  nombrePlan: string;
}

const TablaProgramasTurismo: React.FC<Props> = ({ idPlan, nombrePlan }) => {
  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await axiosInstance.get(`/turismo/programas?planId=${idPlan}`);
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [idPlan]);

  const datosFiltrados = data.filter((item) =>
    busqueda
      ? item.nombre?.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.comunidad?.toLowerCase().includes(busqueda.toLowerCase())
      : true
  );

  const handleVer = (item: any) => { setSelectedItem(item); onOpenVer(); };
  const handleEditar = (item: any) => { setSelectedItem(item); onOpenEditar(); };
  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };
  const handleEliminar = async (item: any) => {
    const ok = window.confirm(`Eliminar el programa ${item.nombre}?`);
    if (!ok) return;
    try {
      await axiosInstance.delete(`/turismo/programas/${item.id}`);
      fetchData();
    } catch {
      // noop
    }
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por nombre o comunidad..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="sm"
            w={{ base: 'full', md: '240px' }}
          />
          <Text fontSize="sm" color="gray.500">Plan: <strong>{nombrePlan}</strong></Text>
        </HStack>
        <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>
          Nuevo programa
        </Button>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Comunidad</Th>
              <Th color="white" fontSize="xs">Tipo</Th>
              <Th color="white" fontSize="xs">Responsable</Th>
              <Th color="white" fontSize="xs">Presupuesto</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : datosFiltrados.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.400">Sin registros</Text></Td></Tr>
            ) : (
              datosFiltrados.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: '#f7efe8' }}>
                  <Td maxW="220px"><Text noOfLines={1} title={item.nombre}>{item.nombre}</Text></Td>
                  <Td>{item.comunidad}</Td>
                  <Td>{item.tipo}</Td>
                  <Td>{item.responsable}</Td>
                  <Td>{item.presupuesto ? `$${Number(item.presupuesto).toLocaleString('es-MX')}` : '-'}</Td>
                  <Td><Badge colorScheme={item.estatus === 'concluido' ? 'blue' : 'green'}>{item.estatus}</Badge></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => handleEditar(item)}>Editar</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminar(item)}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">Detalle del programa</ModalHeader>
          <ModalBody py={5} bg="white">
            {selectedItem && <FormularioProgramaTurismo modo="view" data={selectedItem} idPlan={idPlan} />}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50">
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">Editar programa</ModalHeader>
          <ModalBody py={5} bg="white">
            {selectedItem && <FormularioProgramaTurismo modo="edit" data={selectedItem} idPlan={idPlan} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="gray.50">Nuevo programa</ModalHeader>
          <ModalBody py={5} bg="white">
            <FormularioProgramaTurismo modo="new" idPlan={idPlan} onGuardado={handleGuardado} onCancelar={onCloseNuevo} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaProgramasTurismo;


